import {
  SVSelectProps,
  SearchParamsObject,
  StatusQueryParam,
} from '@/types/common';
import { DefaultValues } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { useGetTeammateListForSelection } from './useTeammates';
import { useGetCompanyListForSelection } from './useCompanies';
import { useGetStaffListForSelection } from './useStaff';
import { useGetCustomerListForSelection } from './useCustomers';
import { useGetBusinessesListForSelection } from './useBusinesses';
import { TeammateRole } from '@/types/teammates';

type UseBuildListFiltersProps = {
  enabled: boolean;
  extraFilters: SVSelectProps[];
  hasCompanyFilter?: boolean;
  hasBusinessFilter?: boolean;
  hasTeammateFilter?: boolean;
  hasStaffFilter?: boolean;
  hasCustomerFilter?: boolean;
};

export default function useBuildListFilters({
  enabled = false,
  extraFilters = [],
  hasCompanyFilter = false,
  hasBusinessFilter = false,
  hasTeammateFilter = false,
  hasStaffFilter = false,
  hasCustomerFilter = false,
}: UseBuildListFiltersProps) {
  const [searchParams] = useSearchParams();

  const { companiesList, isCompaniesListLoading } =
    useGetCompanyListForSelection({}, enabled);
  const { businessList, isBusinessListPending } =
    useGetBusinessesListForSelection(
      { status: StatusQueryParam.ACTIVE },
      enabled,
    );
  const { teammatesList, isTeammatesListPending } =
    useGetTeammateListForSelection(
      { roles: [TeammateRole.ADMIN, TeammateRole.ADVISOR] },
      enabled,
    );
  const { staffList, isStaffListPending } =
    useGetStaffListForSelection(enabled);
  const { customerListByBoth, isCustomerListPending } =
    useGetCustomerListForSelection(
      {
        status: StatusQueryParam.ACTIVE,
      },
      enabled,
    );

  const companyFilter: SVSelectProps = {
    label: 'Company Name',
    name: 'company_id',
    options: hasCompanyFilter ? companiesList : [],
  };

  const businessFilter: SVSelectProps = {
    label: 'Business Name',
    name: 'business_id',
    options: hasBusinessFilter ? businessList : [],
  };

  const teammateFilter: SVSelectProps = {
    label: 'Teammate Name',
    name: 'teammate_id',
    options: hasTeammateFilter ? teammatesList : [],
  };

  const staffFilter: SVSelectProps = {
    label: 'Client Concierge Name',
    name: 'staff_id',
    options: hasStaffFilter ? staffList : [],
  };

  const customerFilter: SVSelectProps = {
    label: 'Owner',
    name: 'consumer_id',
    options: hasCustomerFilter ? customerListByBoth : [],
  };

  const filters = [
    ...extraFilters,
    companyFilter,
    businessFilter,
    teammateFilter,
    staffFilter,
    customerFilter,
  ].filter(filter => filter.options.length >= 1);

  let defaultValues: DefaultValues<SearchParamsObject> = {};

  if (searchParams.size) {
    defaultValues = Object.fromEntries(searchParams);
  } else {
    const newDefaultValues = { ...defaultValues };
    filters.forEach(filter => {
      newDefaultValues[filter.name] = '';
    });

    defaultValues = newDefaultValues;
  }

  return {
    defaultValues,
    filters,
    isLoadingListFilters:
      isCompaniesListLoading ||
      isTeammatesListPending ||
      isStaffListPending ||
      isCustomerListPending ||
      isBusinessListPending,
  };
}
